// Certificates and their expiry (FR-8.6).
//
// Every rule is in the database (20261015172000_certificates.sql): the person,
// HR and their chain read one; the person or HR writes it; the reminder state
// and who recorded it are the server's. A nightly sweep reminds the person and
// HR at 30 and 7 days and on expiry. Links only: the file stays in Drive.
import { supabase } from './supabase';

export type Certificate = {
  id: string;
  profile_id: string;
  name: string;
  issuer: string | null;
  issued_on: string | null;
  expires_on: string | null;
  drive_link: string;
};

export type CertificateInput = Omit<Certificate, 'id' | 'profile_id'>;

const COLS = 'id, profile_id, name, issuer, issued_on, expires_on, drive_link';

export const listCertificates = async (profileId: string): Promise<Certificate[] | null> => {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('certificates')
    .select(COLS)
    .eq('profile_id', profileId)
    .order('expires_on', { ascending: true, nullsFirst: false });
  return error ? null : ((data as Certificate[] | null) ?? []);
};

const clean = (c: CertificateInput) => ({
  name: c.name.trim(),
  issuer: c.issuer?.trim() || null,
  issued_on: c.issued_on || null,
  expires_on: c.expires_on || null,
  drive_link: c.drive_link.trim(),
});

export const saveCertificate = async (
  profileId: string,
  id: string | null,
  c: CertificateInput,
) => {
  if (!supabase) return { error: null };
  const { error } = id
    ? await supabase.from('certificates').update(clean(c)).eq('id', id)
    : await supabase.from('certificates').insert({ profile_id: profileId, ...clean(c) });
  return { error };
};

export const removeCertificate = async (id: string) => {
  if (!supabase) return { error: null };
  const { error } = await supabase.from('certificates').delete().eq('id', id);
  return { error };
};

/**
 * Where a certificate stands against today (an org day key): expired, inside
 * the 30-day reminder window, or fine. Days are whole calendar days.
 */
export const expiryState = (
  expiresOn: string | null,
  today: string,
): { state: 'none' | 'ok' | 'soon' | 'expired'; days: number } => {
  if (!expiresOn) return { state: 'none', days: 0 };
  const days = Math.round(
    (new Date(`${expiresOn}T00:00:00Z`).getTime() - new Date(`${today}T00:00:00Z`).getTime()) /
      86_400_000,
  );
  return { state: days < 0 ? 'expired' : days <= 30 ? 'soon' : 'ok', days };
};
